import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import classNames from "classnames";
import CrossIcon from "../../../../assets/icons/react-svg/CrossIcon";
import Toaster from "../../shared/toaster/Toaster";
import { useLanguage } from "../../../../context/LanguageContext";
import { environment } from "../../../config/environments/environment";
import { submitFeedbackData } from "../../../common/api-services/feedbackApi";
import useJwtToken from "../../../config/auth/useJwtToken";
import {
  formatRemainingTime,
  getFeedbackRateLimitStatus,
  recordFeedbackSubmission,
} from "./feedbackRateLimiter";
import {
  FEEDBACK_ISSUE_OPTIONS,
  MAX_FEEDBACK_LENGTH,
  MAX_FILES,
  MAX_FILE_SIZE_BYTES,
} from "./sidebarFeedback.constants";
import {
  createFeedbackPayload,
  formatFileSize,
  isSupportedAttachment,
  UploadedAttachment,
} from "./sidebarFeedback.utils";
import SidebarFeedbackTypeDropdown from "./SidebarFeedbackTypeDropdown";
import SidebarFeedbackMessageField from "./SidebarFeedbackMessageField";
import SidebarFeedbackAttachments from "./SidebarFeedbackAttachments";
import styles from "./SidebarFeedbackModal.module.css";

type SidebarFeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function SidebarFeedbackModal({
  isOpen,
  onClose,
}: SidebarFeedbackModalProps) {
  const { t } = useLanguage();
  const authUser = useJwtToken();

  const [feedbackType, setFeedbackType] = useState("");
  const [feedbackText, setFeedbackText] = useState("");
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [rateLimitTick, setRateLimitTick] = useState(Date.now());
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState("");
  const [uploadingFileSize, setUploadingFileSize] = useState("");
  const [showFeedbackTypeRequiredError, setShowFeedbackTypeRequiredError] =
    useState(false);
  const [
    showFeedbackMessageRequiredError,
    setShowFeedbackMessageRequiredError,
  ] = useState(false);
  const [toasterState, setToasterState] = useState({
    isOpen: false,
    type: "success" as "success" | "fail",
    message: "",
  });

  const feedbackTypeOptions = useMemo(
    () =>
      FEEDBACK_ISSUE_OPTIONS.map((item, index) => ({
        id: index,
        text: t(item.i18nKey),
        value: item.apiValue,
      })),
    [t],
  );
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const timer = window.setInterval(() => {
      setRateLimitTick(Date.now());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFeedbackType("");
      setFeedbackText("");
      setAttachments([]);
      setIsUploading(false);
      setIsSubmitting(false);
      setUploadProgress(0);
      setUploadingFileName("");
      setUploadingFileSize("");
      setShowFeedbackTypeRequiredError(false);
      setShowFeedbackMessageRequiredError(false);
    }
  }, [isOpen]);

  const rateLimitStatus = useMemo(
    () => getFeedbackRateLimitStatus(rateLimitTick),
    [rateLimitTick],
  );

  const trimmedFeedbackLength = feedbackText.trim().length;
  const isAtMaxLength = feedbackText.length >= MAX_FEEDBACK_LENGTH;
  const isSendDisabled =
    !feedbackType ||
    trimmedFeedbackLength < 1 ||
    isAtMaxLength ||
    isUploading ||
    isSubmitting ||
    rateLimitStatus.isBlocked;

  const showToaster = (message: string, type: "success" | "fail") => {
    setToasterState({ isOpen: true, type, message });
  };

  const handleModalClose = () => {
    onClose();
  };

  const handleFeedbackTextChange = (
    event: ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const nextText = event.target.value.slice(0, MAX_FEEDBACK_LENGTH);
    setFeedbackText(nextText);
    setShowFeedbackMessageRequiredError(false);
  };

  const uploadSingleFile = async (
    file: File,
    onProgress: (progress: number) => void,
  ) => {
    const uploadUrl = `${environment.fileServiceApi}file/uploadfile`;
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(uploadUrl, formData, {
      onUploadProgress: (progressEvent) => {
        const total = progressEvent.total || file.size || 1;
        const progress = Math.round((progressEvent.loaded * 100) / total);
        onProgress(progress);
      },
    });
    return {
      filename: file.name,
      screenshotUrl: `${environment.fileServiceApi}file/${response?.data?.data?.fileDownloadUrl}`,
    };
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";

    if (!selectedFiles.length) return;

    if (attachments.length + selectedFiles.length > MAX_FILES) {
      showToaster(t("feedback_file_count_exceeded"), "fail");
      return;
    }

    const unsupportedFile = selectedFiles.find(
      (file) => !isSupportedAttachment(file),
    );
    if (unsupportedFile) {
      showToaster(t("feedback_unsupported_file_type"), "fail");
      return;
    }

    const oversizedFile = selectedFiles.find(
      (file) => file.size > MAX_FILE_SIZE_BYTES,
    );
    if (oversizedFile) {
      showToaster(t("feedback_file_size_exceeded", { count: 25 }), "fail");
      return;
    }

    const uploadQueue: UploadedAttachment[] = selectedFiles.map(
      (file, index) => ({
        id: `${Date.now()}-${index}-${file.name}`,
        filename: file.name,
        screenshotUrl: "",
        isUploading: true,
        fileSize: formatFileSize(file.size, t),
      }),
    );

    setAttachments((prev) => [...prev, ...uploadQueue]);

    try {
      setIsUploading(true);

      for (let index = 0; index < selectedFiles.length; index++) {
        const file = selectedFiles[index];
        const uploadItem = uploadQueue[index];

        setUploadingFileName(file.name);
        setUploadingFileSize(formatFileSize(file.size, t));
        setUploadProgress(0);

        try {
          const uploadedFile = await uploadSingleFile(file, setUploadProgress);
          setAttachments((prev) =>
            prev.map((item) =>
              item.id === uploadItem.id
                ? {
                    ...item,
                    screenshotUrl: uploadedFile.screenshotUrl,
                    isUploading: false,
                  }
                : item,
            ),
          );
        } catch (error) {
          setAttachments((prev) =>
            prev.filter((item) => item.id !== uploadItem.id),
          );
          showToaster(t("submission_failed_feedback"), "fail");
        }
      }
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadingFileName("");
      setUploadingFileSize("");
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) =>
      prev.filter((_, currentIndex) => currentIndex !== index),
    );
  };

  const handleSubmit = async () => {
    if (!feedbackType) {
      setShowFeedbackTypeRequiredError(true);
      return;
    }
    if (feedbackText.trim().length === 0) {
      setShowFeedbackMessageRequiredError(true);
      return;
    }
    if (isSendDisabled) return;

    try {
      setIsSubmitting(true);
      const payload = createFeedbackPayload(
        authUser,
        feedbackType,
        feedbackText,
        attachments,
      );

      await submitFeedbackData(payload);
      recordFeedbackSubmission();
      showToaster(t("thank_you_feedback"), "success");
      handleModalClose();
    } catch (error) {
      showToaster(t("submission_failed_feedback"), "fail");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen && !toasterState.isOpen) return null;

  const remainingTime = formatRemainingTime(rateLimitStatus.remainingMs);
  const limitMessage = `${t("feedback_limit_reached")}\n${t(
    "feedback_limit_retry",
    {
      time: remainingTime,
    },
  )}`;

  return (
    <>
      {isOpen && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <div className={styles.header}>
              <div>
                <h2 className={styles.title}>{t("help_improve_convay")}</h2>
                <p className={styles.requiredText}>*{t("required_field")}</p>
              </div>
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleModalClose}
                aria-label={t("close")}
              >
                <CrossIcon />
              </button>
            </div>

            <div className={styles.content}>
              <SidebarFeedbackTypeDropdown
                label={t("select_feedback")}
                placeholder={t("select_feedback_type")}
                feedbackType={feedbackType}
                isDropdownOpen={isDropdownOpen}
                showRequiredError={
                  showFeedbackTypeRequiredError && !feedbackType
                }
                dropdownRef={dropdownRef}
                options={feedbackTypeOptions}
                onToggle={() => setIsDropdownOpen((prev) => !prev)}
                onSelect={(value) => {
                  setFeedbackType(value);
                  setIsDropdownOpen(false);
                  setShowFeedbackTypeRequiredError(value === "");
                }}
                requiredErrorText={t("required_field")}
              />

              <SidebarFeedbackMessageField
                label={t("your_feedback")}
                placeholder={t("enter_feedback_here")}
                value={feedbackText}
                maxLength={MAX_FEEDBACK_LENGTH}
                onChange={handleFeedbackTextChange}
                maxLimitMessage={t("maximum_character_limit_reached")}
                showRequiredError={showFeedbackMessageRequiredError}
                requiredErrorText={t("required_field")}
              />

              <SidebarFeedbackAttachments
                label={t("add_attachments")}
                attachments={attachments}
                maxFiles={MAX_FILES}
                isUploading={isUploading}
                uploadingFileName={uploadingFileName}
                uploadingFileSize={uploadingFileSize}
                uploadProgress={uploadProgress}
                uploadingLabel={t("uploading_file")}
                onChange={handleFileChange}
                onRemove={handleRemoveAttachment}
              />

              {rateLimitStatus.isBlocked && (
                <p className={styles.limitText}>{limitMessage}</p>
              )}
            </div>
            <div className={styles.footer}>
              <button
                type="button"
                className={classNames(styles.button, styles.cancelBtn)}
                onClick={handleModalClose}
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                className={classNames(styles.button, styles.sendBtn, {
                  [styles.sendBtnActive]: !isSendDisabled,
                  [styles.sendBtnDisabled]: isSendDisabled,
                })}
                onClick={handleSubmit}
                disabled={isSendDisabled}
              >
                {t("send_feedback")}
              </button>
            </div>
          </div>
        </div>
      )}

      {toasterState.isOpen && (
        <Toaster
          closeToaster={() =>
            setToasterState((prev) => ({ ...prev, isOpen: false }))
          }
          toasterBodyMessage={toasterState.message}
          type={toasterState.type}
          closeToasterTimeout={5000}
        />
      )}
    </>
  );
}
